"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useAuth } from "@/contexts/AuthContext"
import { ArrowLeft } from "lucide-react"
import { toast } from "@/hooks/use-toast"

const Confirmation = () => {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [processing, setProcessing] = useState(true)

  useEffect(() => {
    if (!isAuthenticated || user?.type !== "user") {
      navigate("/user-login")
      return
    }

    // Simulate processing time
    const timer = setTimeout(() => {
      setProcessing(false)
      toast({
        title: "Booking Submitted!",
        description: "Your booking has been submitted for admin approval.",
      })
    }, 2000)

    return () => clearTimeout(timer)
  }, [isAuthenticated, user, navigate])

  if (!user) {
    return <div className="p-6 text-center">Loading...</div>
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button variant="ghost" onClick={() => navigate("/booking")} className="mr-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-blue-900">Booking Confirmation</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="text-center">
          <CardHeader>
            <CardTitle className="text-green-700 text-2xl">
              {processing ? "Processing Your Booking..." : "🎉 Your Booking is Submitted!"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Please wait while we process your booking...</p>
              </>
            ) : (
              <>
                <p className="text-lg text-gray-700">
                  Thank you, <span className="font-semibold">{user.name}</span>!
                </p>
                <p>Your booking has been submitted successfully.</p>
                <p className="text-green-600 font-medium">
                  We've submitted your booking for admin approval. You'll be notified once it's reviewed.
                </p>
                <div className="flex gap-4 justify-center mt-6">
                  
                  <Button onClick={() => navigate("/booking-history")} className="bg-blue-600 hover:bg-blue-700">
                    View Booking History
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/user-dashboard")}>
                    Back to Dashboard
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  )
}

export default Confirmation
